interface UpdateResult {
  success: boolean;
  error?: Error;
  registration?: ServiceWorkerRegistration;
  skipped?: boolean;
  reason?: string;
}

export class ServiceWorkerManager {
  private updateInProgress = false;

  private lastUpdateAttempt: { [scope: string]: number } = {};

  private readonly minUpdateInterval = 30000; // 30 seconds

  private readonly updateTimeout = 10000; // 10 seconds

  // Method for testing purposes to reset state
  resetState(): void {
    this.updateInProgress = false;
    this.lastUpdateAttempt = {};
  }

  async safeUpdateRegistrations(): Promise<UpdateResult[]> {
    if (!navigator?.serviceWorker) {
      return [{ success: false, reason: "ServiceWorker not supported" }];
    }

    if (this.updateInProgress) {
      return [
        {
          success: false,
          skipped: true,
          reason: "Update already in progress",
        },
      ];
    }

    this.updateInProgress = true;

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      const results = await Promise.allSettled(
        registrations.map((reg) => this.updateSingleRegistration(reg))
      );

      return results.map((result, index) => {
        if (result.status === "fulfilled") {
          return result.value;
        }
        return {
          success: false,
          error: result.reason as Error,
          registration: registrations[index],
        };
      });
    } catch (error) {
      return [{ success: false, error: error as Error }];
    } finally {
      this.updateInProgress = false;
    }
  }

  private async updateSingleRegistration(
    reg: ServiceWorkerRegistration
  ): Promise<UpdateResult> {
    try {
      // Validate registration state
      if (!ServiceWorkerManager.isRegistrationValid(reg)) {
        return {
          success: false,
          skipped: true,
          reason: "Registration invalid or redundant",
          registration: reg,
        };
      }

      // Check if update is needed (rate limiting)
      if (!this.shouldUpdate(reg)) {
        return {
          success: false,
          skipped: true,
          reason: "Update attempted too recently",
          registration: reg,
        };
      }

      // Record update attempt time
      this.lastUpdateAttempt[reg.scope] = Date.now();

      // Perform the update with timeout
      await ServiceWorkerManager.updateWithTimeout(reg, this.updateTimeout);

      return {
        success: true,
        registration: reg,
      };
    } catch (error) {
      // Handle specific error types
      if (error instanceof DOMException) {
        if (error.name === "InvalidStateError") {
          // This is the error we're specifically trying to handle gracefully
          return {
            success: false,
            skipped: true,
            reason: "Registration in invalid state (newestWorker is null)",
            registration: reg,
          };
        }
        if (
          error.name === "SecurityError" ||
          error.name === "NotSupportedError"
        ) {
          // Other expected errors that shouldn't be reported to Sentry
          return {
            success: false,
            skipped: true,
            reason: `Expected error: ${error.name}`,
            registration: reg,
          };
        }
      }

      // Re-throw unexpected errors
      throw error;
    }
  }

  private static isRegistrationValid(reg: ServiceWorkerRegistration): boolean {
    // Check if registration exists and has update method
    if (!reg || typeof reg.update !== "function") {
      return false;
    }

    // Check if registration has a valid scope
    if (!reg.scope) {
      return false;
    }

    // Check if registration is being unregistered
    if ("unregistering" in reg && reg.unregistering) {
      return false;
    }

    // Check if registration has at least one worker
    if (!reg.installing && !reg.waiting && !reg.active) {
      return false;
    }

    // Special check for iOS Safari
    if (ServiceWorkerManager.isIOSSafari()) {
      // iOS Safari may have additional state issues
      // Be more conservative with updates
      try {
        // Test if we can access worker properties without throwing
        const hasWorker = !!(reg.active || reg.waiting || reg.installing);
        return hasWorker;
      } catch {
        return false;
      }
    }

    return true;
  }

  private shouldUpdate(reg: ServiceWorkerRegistration): boolean {
    // Don't update if already installing
    if (reg.installing) {
      return false;
    }

    // Rate limiting: don't update too frequently
    const now = Date.now();
    const lastUpdate = this.lastUpdateAttempt[reg.scope];

    if (lastUpdate) {
      const timeSinceLastUpdate = now - lastUpdate;
      if (timeSinceLastUpdate < this.minUpdateInterval) {
        return false;
      }
      // Clean up old entries to prevent memory leaks
      // Remove entries older than 10x the min update interval
      if (timeSinceLastUpdate > this.minUpdateInterval * 10) {
        delete this.lastUpdateAttempt[reg.scope];
      }
    }

    return true;
  }

  private static async updateWithTimeout(
    reg: ServiceWorkerRegistration,
    timeout: number
  ): Promise<void> {
    return await Promise.race([
      reg.update(),
      new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error("Update timeout")), timeout);
      }),
    ]);
  }

  private static isIOSSafari(): boolean {
    const ua = window.navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
    const webkit = /WebKit/.test(ua);
    const safari = /Safari/.test(ua);
    // Chrome and Firefox on iOS have different user agent strings
    const chrome = /CriOS|Chrome/.test(ua);
    const firefox = /FxiOS/.test(ua);

    // Only return true for actual Safari on iOS, not Chrome or Firefox
    return iOS && webkit && safari && !chrome && !firefox;
  }

  static isExpectedError(error: Error): boolean {
    if (!(error instanceof DOMException)) {
      return false;
    }

    const expectedErrorNames = [
      "InvalidStateError",
      "SecurityError",
      "NotSupportedError",
      "AbortError",
      "NetworkError",
    ];

    return expectedErrorNames.includes(error.name);
  }
}

export const serviceWorkerManager = new ServiceWorkerManager();